import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Mail, Instagram } from 'lucide-react';

// Custom TikTok icon component
const TikTokIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.1z"/>
  </svg>
);

export const Footer3D = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const currentYear = new Date().getFullYear();

  return (
    <footer ref={ref} className="relative bg-dark border-t border-gold/20 py-12 md:py-16 px-6 overflow-hidden">
      <motion.div
        className="absolute -top-32 left-1/2 -translate-x-1/2 w-96 h-96 border border-[#c6a04a]/10 rounded-full"
        animate={{
          rotate: [0, 360],
          scale: [1, 1.1, 1]
        }}
        transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
      />

      <div className="max-w-6xl mx-auto relative z-10">
        <div className="grid md:grid-cols-3 gap-12 mb-12">
          <motion.div
            className="space-y-4"
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.1, duration: 0.6 }}
          >
            <motion.h3 
              className="font-display text-4xl uppercase tracking-tight text-gold inline-block"
              whileHover={{ 
                scale: 1.1, 
                textShadow: '0 0 30px rgba(198, 160, 74, 0.8)' 
              }}
            >
              CT3
            </motion.h3>
            <p className="font-body text-sm text-[#e8e6df]/80 font-light leading-relaxed">
              Performance Coaching LLC
              <br />
              Remote 1:1 Coaching
              <br />
              Nashville, TN
            </p>
          </motion.div>

          <motion.div
            className="space-y-4"
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <h4 className="font-body text-sm uppercase tracking-wider text-gold-soft font-semibold">
              Quick Links
            </h4>
            <ul className="space-y-2">
              {[
                { label: 'Apply Now', href: '#apply' },
                { label: 'The Framework', href: '#framework' },
                { label: 'Client Results', href: '#results' }
              ].map((link, i) => (
                <motion.li 
                  key={i}
                  whileHover={{ x: 5 }}
                >
                  <a
                    href={link.href}
                    className="font-body text-sm text-[#e8e6df]/80 hover:text-gold transition-colors duration-300 font-light"
                  >
                    {link.label}
                  </a>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            className="space-y-4"
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            <h4 className="font-body text-sm uppercase tracking-wider text-gold-soft font-semibold">
              Get in Touch
            </h4>
            <div className="space-y-3">
              <motion.a
                href="mailto:coach@ct3performance.com"
                className="flex items-center space-x-2 text-[#e8e6df]/80 hover:text-gold transition-colors duration-300 group"
                whileHover={{ x: 5 }}
              >
                <Mail className="w-4 h-4 group-hover:scale-110 transition-transform duration-300" />
                <span className="font-body text-sm font-light">coach@ct3performance.com</span>
              </motion.a>
              
              <div className="flex space-x-4 pt-2">
                <motion.a
                  href="https://www.instagram.com/cliftontantfitness?utm_source=qr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 border border-gold/30 hover:border-gold flex items-center justify-center transition-all duration-300 hover:bg-gold/10"
                  whileHover={{ scale: 1.15, rotateY: 180 }}
                  whileTap={{ scale: 0.9 }}
                  aria-label="Instagram - @cliftontantfitness"
                >
                  <Instagram className="w-5 h-5 text-gold" />
                </motion.a>
                <motion.a
                  href="https://www.tiktok.com/@ct3lifestylecoach?_r=1&_t=ZT-96jQqtNnvCw"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 border border-gold/30 hover:border-gold flex items-center justify-center transition-all duration-300 hover:bg-gold/10"
                  whileHover={{ scale: 1.15, rotateY: 180 }}
                  whileTap={{ scale: 0.9 }}
                  aria-label="TikTok - @ct3lifestylecoach"
                >
                  <TikTokIcon className="w-5 h-5 text-gold" />
                </motion.a>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div 
          className="pt-8 border-t border-gold/10 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0"
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.5, duration: 0.6 }}
        >
          <p className="font-body text-xs text-[#e8e6df]/60 font-light">
            © {currentYear} CT3 Performance Coaching LLC. All rights reserved.
          </p>
          <p className="font-body text-xs text-gold-soft uppercase tracking-wider">
            By Application Only
          </p>
        </motion.div>
      </div>
    </footer>
  );
};
